import { Alert, Badge, Tooltip } from "@mantine/core";
import type { MantineSize } from "@mantine/core";
import { IconAlertTriangle, IconDatabaseOff } from "@tabler/icons-react";
import { describeGeneration, keyExposure } from "@/wallet/generations";
import type { SessionKeyPermissionDoc } from "@/firestore/types";
import { isMissingIndexError, isPermissionError } from "@/lib/errors";
import { ErrorState } from "./states";

/** A wallet's contract generation: v3.8 green, v3.7 orange, deprecated red, unknown gray. */
export function GenerationBadge({
  generation,
  size = "sm",
}: {
  generation: unknown;
  size?: MantineSize;
}) {
  const d = describeGeneration(generation);
  return (
    <Tooltip label={d.description} multiline w={340} withArrow>
      <Badge
        color={d.color}
        variant={d.tone === "unknown" ? "outline" : "light"}
        radius="sm"
        tt="none"
        size={size}
        data-testid="generation-badge"
      >
        {d.label}
      </Badge>
    </Tooltip>
  );
}

/** The Keys panel's exposure chip ("Legacy, 1271-capable" warning, or "User ops only"). */
export function KeyExposureChip({ permission }: { permission: Partial<SessionKeyPermissionDoc> }) {
  const exposure = keyExposure(permission);
  return (
    <Tooltip label={exposure.detail} multiline w={360} withArrow>
      <Badge
        color={exposure.color}
        variant={exposure.signatureCapable ? "filled" : "light"}
        radius="sm"
        tt="none"
        leftSection={exposure.signatureCapable ? <IconAlertTriangle size={12} /> : undefined}
      >
        {exposure.label}
      </Badge>
    </Tooltip>
  );
}

/**
 * Read error on one of the admin-only collections the v3.8 rollout added. Until the backend
 * release, its Firestore rules (default-deny ⇒ permission error) and its indexes reach an
 * environment, the collection cannot be read: say so instead of a raw error.
 */
export function AdminProjectionErrorState({
  error,
  title,
  collections,
  release,
}: {
  error: Error;
  title: string;
  collections: string;
  release: string;
}) {
  if (isPermissionError(error)) {
    return (
      <Alert color="gray" variant="light" icon={<IconDatabaseOff size={18} />} title={title}>
        {collections} {release}. They read here once that release, its Firestore rules and its
        projection backfill reach this environment. If they have, sign out and back in.
      </Alert>
    );
  }
  if (isMissingIndexError(error)) {
    return (
      <Alert
        color="orange"
        variant="light"
        icon={<IconAlertTriangle size={18} />}
        title="Missing Firestore index"
      >
        This query needs a composite index from elios-backend
        infrastructure/backend/firestore/indexes.tf that is not applied in this environment yet.{" "}
        {error.message}
      </Alert>
    );
  }
  return <ErrorState error={error} />;
}

/** Read error on the key inventory (`sessionKeyPermissions`, `walletKeyInventories`). */
export function KeyInventoryErrorState({ error }: { error: Error }) {
  return (
    <AdminProjectionErrorState
      error={error}
      title="Key inventory not available in this environment yet"
      collections="sessionKeyPermissions and walletKeyInventories are admin-only collections"
      release="added by wallet-service 0.74.0"
    />
  );
}

/** Read error on `walletUpgradeOffers`. */
export function UpgradeOffersErrorState({ error }: { error: Error }) {
  return (
    <AdminProjectionErrorState
      error={error}
      title="Upgrade offers not available in this environment yet"
      collections="walletUpgradeOffers is an admin-only collection"
      release="added by wallet-service 0.75.0"
    />
  );
}
